import type { AuthUser } from "@/features/auth/store/auth-store";

/** Org-role authority rank, mirroring the backend's `ROLE_RANK`. Platform users
 * (GA/SA) outrank every org role. The single source of truth for the UI's
 * role gates (sidebar nav, route guards, the BP assign picker) — keep in step
 * with `apps/accounts/models.py::User.ROLE_RANK`. */
export const ORG_RANK: Record<string, number> = {
  staff: 1,
  team_leader: 2,
  country_director: 3,
};

/** A Global/Super Admin — cross-org platform authority, above any org role. */
export function isPlatformUser(user: AuthUser | null): boolean {
  return !!user && ((user.is_superuser ?? false) || (user.is_platform_admin ?? false));
}

/** The top-of-hierarchy Global Admin specifically (Django superuser) — distinct
 * from a Super Admin (`is_platform_admin`). Mirrors the backend `IsGlobalAdmin`
 * gate, where GA ⊋ SA (`apps/core/permissions.py`). */
export function isGlobalAdmin(user: AuthUser | null): boolean {
  return !!user?.is_superuser;
}

/** Numeric authority rank: 99 for platform users, else the org-role rank (0 if none). */
export function orgRoleRank(user: AuthUser | null): number {
  if (isPlatformUser(user)) return 99;
  return (user?.role && ORG_RANK[user.role]) || 0;
}

/** May bypass the New-Arrival pipeline when adding a client (the "Skip
 * onboarding" option). Limited to a Super Admin / GA and the NSI house Country
 * Director — not any Business-Practitioner role. Mirrors the backend guard in
 * `apps/clients/serializers.py`. */
export function canSkipOnboarding(user: AuthUser | null): boolean {
  if (isPlatformUser(user)) return true;
  return user?.role === "country_director" && user?.firm?.org_type === "house";
}

/** Mirrors the backend role-grant matrix (`_can_grant_role`): GA/SA assign
 * anyone; otherwise a user may assign only roles ranked strictly below their own
 * (CD → TL/Staff, TL → Staff, Staff → nobody). */
export function canAssignRole(user: AuthUser | null, role: string | null): boolean {
  if (isPlatformUser(user)) return true;
  const target = (role && ORG_RANK[role]) || 0;
  return target > 0 && target < orgRoleRank(user);
}
